import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, Clock, Phone, ChevronDown } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About", children: [
      { label: "About Us", to: "/about" },
      { label: "Temple History", to: "/about/history" },
      { label: "Origin & Connection", to: "/about/origin" },
      { label: "Our Journey", to: "/about/journey" },
    ],
  },
  {
    label: "Worship", children: [
      { label: "Deities & Sacred Idols", to: "/worship/deities" },
      { label: "Darshan & Timings", to: "/worship/timings" },
    ],
  },
  { label: "Seva", to: "/seva" },
  {
    label: "Happenings", children: [
      { label: "Events & Festivals", to: "/happenings/events" },
      { label: "Annual Utsav", to: "/happenings/utsav" },
      { label: "News & Announcements", to: "/happenings/news" },
    ],
  },
  {
    label: "Community", children: [
      { label: "Social Service", to: "/community/social-service" },
      { label: "Gallery", to: "/community/gallery" },
    ],
  },
  {
    label: "Help", children: [
      { label: "FAQ", to: "/help/faq" },
      { label: "Contact Us", to: "/help/contact" },
    ],
  },
];

function Dropdown({ item }) {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <NavLink to={item.to} end={item.to === "/"}
        className={({ isActive }) =>
          `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${isActive ? "text-orange-700" : "text-stone-700 hover:text-orange-700"}`}
        data-testid={`nav-${item.label.toLowerCase()}`}>
        {item.label}
      </NavLink>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-stone-700 hover:text-orange-700 transition-colors"
        data-testid={`nav-${item.label.toLowerCase()}`}>
        {item.label} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-1 w-60 z-50">
          <div className="bg-white rounded-xl shadow-xl border border-amber-200/70 overflow-hidden py-1 animate-fade-up">
            {item.children.map((c) => (
              <Link key={c.to} to={c.to}
                className="block px-4 py-2.5 text-sm text-stone-700 hover:bg-amber-50 hover:text-orange-700 transition-colors"
                data-testid={`nav-sub-${c.to.replace(/\//g, "-")}`}>
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const s = useSettings();
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setShowSearch(false); setMobile(false); }
  };

  return (
    <header className={`sticky top-0 z-40 transition-all ${scrolled ? "bg-amber-50/95 shadow-md" : "bg-amber-50/80"} backdrop-blur-md border-b border-amber-200/60`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20 gap-3">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <img src={s.logo_url} alt="Temple logo" className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-400/50" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display text-lg font-extrabold text-orange-800">{s.temple_name || "Sri Mehandipur Balaji Mandir"}</div>
              <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">{s.temple_tagline || "Rajarajeshwari Nagar, Bengaluru"}</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center">
            {NAV.map((i) => <Dropdown key={i.label} item={i} />)}
          </nav>

          <div className="flex items-center gap-2">
            <button onClick={() => setShowSearch((v) => !v)} className="p-2 text-stone-600 hover:text-orange-700" data-testid="header-search-btn" aria-label="Search">
              <Search className="w-5 h-5" />
            </button>
            <Link to="/worship/timings" className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-stone-700 hover:text-orange-700" data-testid="header-timings-cta">
              <Clock className="w-4 h-4" /> Timings
            </Link>
            <Link to="/help/contact" className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-stone-700 hover:text-orange-700" data-testid="header-contact-cta">
              <Phone className="w-4 h-4" /> Contact
            </Link>
            <span className="hidden md:inline text-xs font-semibold text-amber-700 border border-amber-300 rounded-full px-2 py-1" title="More languages coming soon">EN</span>
            <button className="xl:hidden p-2 text-stone-700" onClick={() => setMobile((v) => !v)} data-testid="header-mobile-toggle" aria-label="Menu">
              {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {showSearch && (
          <form onSubmit={doSearch} className="pb-3 animate-fade-up">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deities, seva, events, news, FAQ…"
              className="w-full rounded-full border border-amber-300 px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="header-search-input" autoFocus />
          </form>
        )}
      </div>

      {mobile && (
        <div className="xl:hidden bg-amber-50 border-t border-amber-200 max-h-[75vh] overflow-y-auto animate-fade-up">
          <div className="px-4 py-3 space-y-1">
            {NAV.map((i) =>
              i.children ? (
                <details key={i.label} className="group">
                  <summary className="flex items-center justify-between py-2.5 font-semibold text-stone-800 cursor-pointer list-none">
                    {i.label} <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="pl-4 pb-2 space-y-1">
                    {i.children.map((c) => (
                      <Link key={c.to} to={c.to} onClick={() => setMobile(false)} className="block py-2 text-stone-600" data-testid={`mnav-${c.to.replace(/\//g, "-")}`}>{c.label}</Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link key={i.to} to={i.to} onClick={() => setMobile(false)} className="block py-2.5 font-semibold text-stone-800" data-testid={`mnav-${i.label.toLowerCase()}`}>{i.label}</Link>
              )
            )}
            <Link to="/seva" onClick={() => setMobile(false)} className="block text-center mt-3 bg-orange-600 text-white font-bold py-3 rounded-full" data-testid="mnav-book-seva">Book Seva</Link>
          </div>
        </div>
      )}
    </header>
  );
}
